import { useEffect, useState } from "react";
import AdminApiFactory from "../../admin-api/AdminApiFactory";
import {
  BusinessSpace,
  OverallAuditRecord,
  OverallAuditResponse,
  OverallAuditStats,
  SegmentAuditSummary,
} from "../../admin-api/generated-src";
import {
  OVERALL_AUDIT_MAX_PAGES,
  OVERALL_AUDIT_MAX_RECORDS,
  OVERALL_AUDIT_PAGE_SIZE,
} from "../../constants";

/**
 * Shape returned by useOverallAuditData. `records` is the merged set of scans across
 * all segments; `stats` and `segmentBreakdown` describe the whole plan group.
 */
export interface OverallAuditData {
  stats: OverallAuditStats;
  segmentBreakdown: SegmentAuditSummary[];
  records: OverallAuditRecord[];
  loading: boolean;
  error: boolean;
  /** True when the API returned 403 — surfaced as an auth-specific message, matching the per-segment path. */
  unauthorized: boolean;
  /** True when paging stopped at a safety cap before the backend signalled completion. */
  truncated: boolean;
}

const EMPTY_DATA: OverallAuditData = {
  stats: {},
  segmentBreakdown: [],
  records: [],
  loading: false,
  error: false,
  unauthorized: false,
  truncated: false,
};

/**
 * Fetches GET /admin/audit/overall for a (nodeId, planGroupId, businessSpace) and assembles
 * every page into a single in-memory dataset, so the existing client-side AuditDetailsTable
 * can sort/filter/paginate across the full set rather than one page at a time. The server-side
 * cursor contract (nextToken + pageSize) is honoured exactly — assembly just means looping
 * until nextToken is null. The fetch strategy is confined to this hook (plus a flag on the
 * table), so switching to true server-side paging later would not touch the tab/stats/breakdown.
 *
 * Safety:
 *  - Hard caps on pages (OVERALL_AUDIT_MAX_PAGES) and records (OVERALL_AUDIT_MAX_RECORDS), plus
 *    a repeated-token break, so a stable/duplicate nextToken can never spin forever.
 *  - A per-run `active` guard discards results if a newer run (refresh, plan-group or
 *    business-space change) supersedes this one, so loops can't interleave into a stale buffer.
 *  - stats/segmentBreakdown are captured from the first page that provides them and never
 *    overwritten by a later page (defensive against an empty later page zeroing them out).
 *
 * @param nodeId         sort-center node id (from the route)
 * @param planGroupId    selected plan group; when undefined the hook is idle
 * @param businessSpace  selected business space (required by the API)
 * @param refreshToken   flips when the audit page requests a refresh (timer or button);
 *                       a change re-runs the fetch
 */
export default function useOverallAuditData(
  nodeId: string | undefined,
  planGroupId: string | undefined,
  businessSpace: BusinessSpace | undefined,
  refreshToken: boolean,
): OverallAuditData {
  const [data, setData] = useState<OverallAuditData>(EMPTY_DATA);

  useEffect(() => {
    // Idle until we have the params the API requires.
    if (!nodeId || !planGroupId || !businessSpace) {
      setData(EMPTY_DATA);
      return;
    }

    let active = true;
    const AdminApi = AdminApiFactory();
    setData({ ...EMPTY_DATA, loading: true });

    (async () => {
      const accumulated: OverallAuditRecord[] = [];
      let stats: OverallAuditStats | undefined;
      let segmentBreakdown: SegmentAuditSummary[] | undefined;
      let nextToken: string | undefined;
      let prevToken: string | undefined;
      let pages = 0;
      let truncated = false;

      try {
        do {
          const response: OverallAuditResponse = (await AdminApi.getOverallAudit(
            nodeId,
            planGroupId,
            businessSpace,
            nextToken,
            OVERALL_AUDIT_PAGE_SIZE,
          )).data as OverallAuditResponse;

          // A newer run started while this request was in flight — drop these results.
          if (!active) return;

          if (response) {
            accumulated.push(...(response.records || []));
            // First non-empty wins; never let a later page overwrite good stats/breakdown.
            if (!stats && response.overallStats) {
              stats = response.overallStats;
            }
            if (!segmentBreakdown && response.segmentBreakdown) {
              segmentBreakdown = response.segmentBreakdown;
            }
            prevToken = nextToken;
            nextToken = response.nextToken || undefined;
          } else {
            // Empty/falsy body on a 200 — surface it the way the per-segment path does
            // (AuditPage.tsx) so an empty audit is traceable, not indistinguishable from
            // "nobody scanned". Only meaningful on the first page (pages === 0).
            if (pages === 0) {
              console.error(
                `Got empty response from /admin/audit/overall for node=${nodeId} planGroup=${planGroupId} businessSpace=${businessSpace}`,
              );
            }
            nextToken = undefined;
          }

          pages += 1;

          // Stop on a repeated cursor or any safety ceiling, and flag truncation.
          // Each break also logs so a partial audit is traceable even if the operator
          // misses the UI alert.
          if (nextToken && nextToken === prevToken) {
            truncated = true;
            console.warn(
              `Overall audit paging stopped: repeated nextToken for node=${nodeId} planGroup=${planGroupId} businessSpace=${businessSpace} (pages=${pages}, records=${accumulated.length})`,
            );
            break;
          }
          if (pages >= OVERALL_AUDIT_MAX_PAGES) {
            truncated = nextToken != null;
            if (truncated) {
              console.warn(
                `Overall audit paging stopped: hit MAX_PAGES for node=${nodeId} planGroup=${planGroupId} businessSpace=${businessSpace} (pages=${pages}, records=${accumulated.length})`,
              );
            }
            break;
          }
          if (accumulated.length >= OVERALL_AUDIT_MAX_RECORDS) {
            truncated = nextToken != null;
            if (truncated) {
              console.warn(
                `Overall audit paging stopped: hit MAX_RECORDS for node=${nodeId} planGroup=${planGroupId} businessSpace=${businessSpace} (pages=${pages}, records=${accumulated.length})`,
              );
            }
            break;
          }
        } while (nextToken);

        if (!active) return;
        setData({
          stats: stats || {},
          segmentBreakdown: segmentBreakdown || [],
          records: accumulated,
          loading: false,
          error: false,
          unauthorized: false,
          truncated,
        });
      } catch (ex: any) {
        if (!active) return;
        const unauthorized = ex?.response?.status === 403;
        console.error(
          `Error fetching overall audit for node=${nodeId} planGroup=${planGroupId} businessSpace=${businessSpace}` +
            (unauthorized ? " (403 unauthorized)" : ""),
          ex,
        );
        // Match the per-segment path: 403 → auth-specific message, others → generic error.
        setData({ ...EMPTY_DATA, error: true, unauthorized });
      }
    })();

    return () => {
      active = false;
    };
  }, [nodeId, planGroupId, businessSpace, refreshToken]);

  return data;
}
